import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback } from 'react';
import { WebrtcState } from 'sdk';

import { useSDK } from '../../app';
import { styled } from '../../styled';
import { isMobile } from '../../utils/isMobile';
import { VIDEO_CALLS_ARE_SUPPORTED } from '../../utils/isVideoCallsSupported';
import { CloseChatIcon } from './closeChatIcon';
import { MessageIcon } from './messageIcon';
import { StartVideoIcon } from './startVideoIcon';
import { UnreadCount } from './unreadCount';

type Interface = {
	className: string;
};

const styles = css`
	align-items: center;
	display: flex;
	flex-direction: column;
	float: right;
	grid-gap: 10px;
	justify-content: center;
	position: relative;
	text-align: center;

	& > div {
		cursor: pointer;
	}

	& > .icon {
		position: relative;
	}

	& .unread-count {
		position: absolute;
	}
`;

const Component = ({ className }: Interface): ReactElement | null => {
	const { uiStateStore, eventsStore, welcomeDataStore, videoCallsStore, showcaseStatusStore } = useSDK();
	const onClickHandler = useCallback(() => {
		uiStateStore.setOpened(!uiStateStore.opened);
	}, [uiStateStore]);
	const showcase = welcomeDataStore.data?.showcase;

	if (!uiStateStore.showAllShowcase) {
		uiStateStore.showcaseIsClosedLog();
		return null;
	}

	const CloseChatIconComponent = (): null | ReactElement => {
		if (isMobile) {
			return null;
		}
		return <CloseChatIcon />;
	};

	return (
		<div className={className}>
			{!uiStateStore.opened &&
				showcase?.isOnline &&
				showcase?.useVideo &&
				// outer start video button only available when startWithVideo enabled
				showcase?.startWithVideo &&
				videoCallsStore.isVideoCallsEnabled &&
				VIDEO_CALLS_ARE_SUPPORTED &&
				!showcaseStatusStore.blocked &&
				videoCallsStore.state === WebrtcState.off && <StartVideoIcon />}
			<div onClick={onClickHandler} className="icon">
				{uiStateStore.opened ? <CloseChatIconComponent /> : <MessageIcon />}
				{!uiStateStore.opened && (eventsStore.unreadMessages > 0 || null) && (
					<UnreadCount className="unread-count" />
				)}
			</div>
		</div>
	);
};

const OpenWidgetIcon = styled(observer(Component))(styles);

export { OpenWidgetIcon };
